import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  CreditCard, 
  AlertTriangle, 
  CheckCircle, 
  Users, 
  Calendar,
  Hash,
  Euro,
  Bell,
  Loader2,
  Plus,
  RefreshCw,
  Edit3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentsPage = () => {
  const { t } = useLanguage();
  const [clients, setClients] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [setupDialog, setSetupDialog] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [renewDialog, setRenewDialog] = useState(false);
  const [updateSessionsDialog, setUpdateSessionsDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Setup form
  const [paymentType, setPaymentType] = useState('monthly');
  const [monthlyPrice, setMonthlyPrice] = useState('');
  const [paymentDay, setPaymentDay] = useState('1');
  const [bonusSessions, setBonusSessions] = useState('10');

  // Payment record form
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  
  // Update sessions form
  const [newSessionsRemaining, setNewSessionsRemaining] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, alertsRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/payments/alerts`)
      ]);
      setClients(clientsRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupPayment = async () => {
    setSubmitting(true);
    try {
      const data = {
        client_id: selectedClient.id,
        payment_type: paymentType,
        ...(paymentType === 'monthly' ? {
          monthly_price: parseFloat(monthlyPrice),
          payment_day: parseInt(paymentDay)
        } : {
          bonus_sessions: parseInt(bonusSessions)
        })
      };
      await axios.post(`${API}/payments/setup`, data);
      toast.success('Configuración guardada');
      setSetupDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Error al configurar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecordPayment = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/payments/record`, {
        client_id: selectedClient.id,
        amount: paymentAmount ? parseFloat(paymentAmount) : null,
        notes: paymentNotes
      });
      toast.success('Pago registrado');
      setPaymentDialog(false);
      setPaymentAmount('');
      setPaymentNotes('');
      fetchData();
    } catch (error) {
      toast.error('Error al registrar pago');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRenewBonus = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/payments/renew-bonus`, {
        client_id: selectedClient.id,
        payment_type: 'bonus',
        bonus_sessions: parseInt(bonusSessions)
      });
      toast.success('Bono renovado');
      setRenewDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Error al renovar bono');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateSessions = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/payments/update-sessions`, {
        client_id: selectedClient.id,
        sessions_remaining: parseInt(newSessionsRemaining)
      });
      toast.success(`Sesiones actualizadas a ${newSessionsRemaining}`);
      setUpdateSessionsDialog(false);
      setNewSessionsRemaining('');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar sesiones');
    } finally {
      setSubmitting(false);
    }
  };

  const openUpdateSessionsDialog = (client) => {
    setSelectedClient(client);
    const info = client.payment_info || {};
    setNewSessionsRemaining(info.bonus_remaining?.toString() || '0');
    setUpdateSessionsDialog(true);
  };

  const openSetupDialog = (client) => {
    setSelectedClient(client);
    const info = client.payment_info || {};
    setPaymentType(info.payment_type || 'monthly');
    setMonthlyPrice(info.monthly_price?.toString() || '');
    setPaymentDay(info.payment_day?.toString() || '1');
    setBonusSessions(info.bonus_total?.toString() || '10');
    setSetupDialog(true);
  };

  const getStatusBadge = (client) => {
    const info = client.payment_info;
    if (!info) {
      return <span className="px-2 py-1 text-xs bg-gray-500/20 text-gray-400 rounded">Sin configurar</span>;
    }
    
    if (info.payment_type === 'monthly') {
      return (
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 text-xs bg-blue-500/20 text-blue-400 rounded flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            Mensual {info.monthly_price}€
          </span>
        </div>
      );
    } else if (info.payment_type === 'bonus') {
      const remaining = info.bonus_remaining || 0;
      const total = info.bonus_total || 0;
      const percentage = total > 0 ? (remaining / total) * 100 : 0;
      return (
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${
            remaining <= 2 ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
          }`}>
            <Hash className="h-3 w-3" />
            {remaining}/{total} sesiones
          </span>
        </div>
      );
    }
  };

  const alertClients = alerts.map(a => a.client_id);

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in" data-testid="payments-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Control de Pagos</h1>
            <p className="text-muted-foreground">{clients.length} clientes</p>
          </div>
        </div>

        {/* Alerts Section */}
        {alerts.length > 0 && (
          <Card className="bg-card border-red-500/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-red-400">
                <Bell className="h-5 w-5" />
                Alertas de Pago ({alerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {alerts.map((alert, index) => (
                  <div 
                    key={index}
                    className={`flex items-center justify-between p-3 rounded-sm ${
                      alert.severity === 'high' ? 'bg-red-500/10 border border-red-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <AlertTriangle className={`h-5 w-5 ${
                        alert.severity === 'high' ? 'text-red-400' : 'text-yellow-400'
                      }`} />
                      <div>
                        <p className="font-medium text-foreground">{alert.client_name}</p>
                        <p className="text-sm text-muted-foreground">{alert.message}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {alert.type === 'monthly' ? (
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedClient(clients.find(c => c.id === alert.client_id));
                            setPaymentDialog(true);
                          }}
                          className="bg-primary text-primary-foreground"
                        >
                          <Euro className="h-4 w-4 mr-1" />
                          Registrar Pago
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedClient(clients.find(c => c.id === alert.client_id));
                            setBonusSessions('10');
                            setRenewDialog(true);
                          }}
                          className="bg-primary text-primary-foreground"
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Renovar Bono
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Clients List */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg">Clientes</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : clients.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No hay clientes</p>
              </div>
            ) : (
              <div className="space-y-3">
                {clients.map((client) => {
                  const hasAlert = alertClients.includes(client.id);
                  const info = client.payment_info || {};
                  
                  return (
                    <div 
                      key={client.id}
                      className={`flex items-center justify-between p-4 rounded-sm border ${
                        hasAlert ? 'border-red-500/30 bg-red-500/5' : 'border-border bg-surface'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Users className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{client.name}</p>
                          <p className="text-sm text-muted-foreground">{client.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {getStatusBadge(client)}
                        
                        <div className="flex gap-2">
                          {info.payment_type === 'monthly' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedClient(client);
                                setPaymentAmount(info.monthly_price?.toString() || '');
                                setPaymentDialog(true);
                              }}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Pago
                            </Button>
                          )}
                          
                          {info.payment_type === 'bonus' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                                onClick={() => openUpdateSessionsDialog(client)}
                                data-testid={`update-sessions-${client.id}`}
                              >
                                <Edit3 className="h-4 w-4 mr-1" />
                                Sesiones
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedClient(client);
                                  setBonusSessions(info.bonus_total?.toString() || '10');
                                  setRenewDialog(true);
                                }}
                              >
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Renovar
                              </Button>
                            </>
                          )}
                          
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openSetupDialog(client)}
                          >
                            <CreditCard className="h-4 w-4 mr-1" />
                            Configurar
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Setup Dialog */}
        <Dialog open={setupDialog} onOpenChange={setSetupDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Configurar Pago - {selectedClient?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Tipo de pago</Label>
                <Select value={paymentType} onValueChange={setPaymentType}>
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensual</SelectItem>
                    <SelectItem value="bonus">Bono de sesiones</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {paymentType === 'monthly' ? (
                <>
                  <div className="space-y-2">
                    <Label>Precio mensual (€)</Label>
                    <Input
                      type="number"
                      value={monthlyPrice}
                      onChange={(e) => setMonthlyPrice(e.target.value)}
                      placeholder="50"
                      className="bg-zinc-900/50 border-zinc-800"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Día de pago (del mes)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="31"
                      value={paymentDay}
                      onChange={(e) => setPaymentDay(e.target.value)}
                      className="bg-zinc-900/50 border-zinc-800"
                    />
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <Label>Número de sesiones</Label>
                  <Select value={bonusSessions} onValueChange={setBonusSessions}>
                    <SelectTrigger className="bg-zinc-900/50 border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 sesiones</SelectItem>
                      <SelectItem value="20">20 sesiones</SelectItem>
                      <SelectItem value="30">30 sesiones</SelectItem>
                      <SelectItem value="40">40 sesiones</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSetupDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSetupPayment}
                className="bg-primary text-primary-foreground"
                disabled={submitting}
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Record Payment Dialog */}
        <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Registrar Pago - {selectedClient?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Cantidad (€)</Label>
                <Input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder={selectedClient?.payment_info?.monthly_price?.toString() || '0'}
                  className="bg-zinc-900/50 border-zinc-800"
                />
              </div>
              <div className="space-y-2">
                <Label>Notas (opcional)</Label>
                <Input
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="Transferencia, Bizum..."
                  className="bg-zinc-900/50 border-zinc-800"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPaymentDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleRecordPayment}
                className="bg-primary text-primary-foreground"
                disabled={submitting}
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Registrar Pago'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Renew Bonus Dialog */}
        <Dialog open={renewDialog} onOpenChange={setRenewDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Renovar Bono - {selectedClient?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Número de sesiones</Label>
                <Select value={bonusSessions} onValueChange={setBonusSessions}>
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 sesiones</SelectItem>
                    <SelectItem value="20">20 sesiones</SelectItem>
                    <SelectItem value="30">30 sesiones</SelectItem>
                    <SelectItem value="40">40 sesiones</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRenewDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleRenewBonus}
                className="bg-primary text-primary-foreground"
                disabled={submitting}
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Renovar Bono'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Update Sessions Dialog */}
        <Dialog open={updateSessionsDialog} onOpenChange={setUpdateSessionsDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit3 className="h-5 w-5 text-amber-400" />
                Actualizar Sesiones - {selectedClient?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Sesiones actuales: <span className="font-bold text-foreground">{selectedClient?.payment_info?.bonus_remaining || 0}</span> / {selectedClient?.payment_info?.bonus_total || 0}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Nuevo número de sesiones restantes</Label>
                <Input
                  type="number"
                  min="0"
                  value={newSessionsRemaining}
                  onChange={(e) => setNewSessionsRemaining(e.target.value)}
                  placeholder="0"
                  className="bg-zinc-900/50 border-zinc-800 text-xl font-bold text-center"
                />
                <p className="text-xs text-muted-foreground text-center">
                  Puedes sumar o restar sesiones según necesites
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUpdateSessionsDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleUpdateSessions}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                disabled={submitting || !newSessionsRemaining}
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Actualizar Sesiones'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default PaymentsPage;
